import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import weight from '../assets/weight.svg';
import quantity from '../assets/quantity.svg';
import box from '../assets/box.svg';
import length from '../assets/length.svg';
import { QuoteContext } from '../assets/context/QuoteContext';
import { useAuth } from '../assets/context/AuthContext';
import url from './url';
import BookingOutcomeNotice from './BookingOutcomeNotice';

const valueOrFallback = (value, fallback = '—') => (
    value === null || value === undefined || value === '' ? fallback : value
);

const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const amount = Number(value);
    return Number.isNaN(amount)
        ? `${value} AUD`
        : new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);
};

const formatDate = (value) => {
    if (!value) return 'Not available';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    }).format(date);
};

const statusClass = (status = '') => status.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-');

const getRouteLabel = (country, city, postcode) => (
    [city, country, postcode].filter(Boolean).join(', ') || 'Location not set'
);

function Bookings() {
    const navigate = useNavigate();
    const location = useLocation();
    const { restoreDraft, deleteDraft, applyBookingTemplate } = useContext(QuoteContext);
    const { accountType } = useAuth();
    const isBusiness = accountType === 'business';

    const [bookings, setBookings] = useState([]);
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [draftError, setDraftError] = useState('');
    const [deletingDraftId, setDeletingDraftId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState(
        location.state?.draftSaved ? 'drafts' : 'all'
    );

    // Business profile + markup state
    const [bizProfile, setBizProfile] = useState(null);
    const [markup, setMarkup] = useState('');
    const [markupInput, setMarkupInput] = useState('');
    const [isSavingMarkup, setIsSavingMarkup] = useState(false);
    const [markupFeedback, setMarkupFeedback] = useState('');
    const [markupError, setMarkupError] = useState('');

    useEffect(() => {
        const fetchAccountData = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Please sign in to view your bookings and drafts.');
                setLoading(false);
                return;
            }

            try {
                const headers = {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                };
                const fetches = [
                    fetch(`${url}/api/booking/fetch-bookings`, { method: 'POST', headers }),
                    fetch(`${url}/api/drafts`, { headers }),
                ];
                if (isBusiness) fetches.push(fetch(`${url}/api/business/profile`, { headers }));

                const [bookingsResponse, draftsResponse, profileResponse] = await Promise.all(fetches);
                const bookingsData = await bookingsResponse.json().catch(() => ({}));
                const draftsData = await draftsResponse.json().catch(() => ({}));

                if (!bookingsResponse.ok && bookingsResponse.status !== 404) {
                    throw new Error(bookingsData.message || 'Failed to load bookings.');
                }
                if (!draftsResponse.ok) {
                    throw new Error(draftsData.message || 'Failed to load drafts.');
                }

                setBookings(bookingsData.bookings || []);
                setDrafts(draftsData.drafts || []);

                if (profileResponse) {
                    const profileData = await profileResponse.json().catch(() => ({}));
                    if (profileResponse.ok) {
                        setBizProfile(profileData);
                        const val = profileData.Business_Markup ?? '';
                        setMarkup(val);
                        setMarkupInput(val);
                    }
                }
            } catch (fetchError) {
                setError(fetchError.message || 'Unable to load your account.');
            } finally {
                setLoading(false);
            }
        };

        fetchAccountData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBusiness]);

    const handleMarkupSave = async (e) => {
        e.preventDefault();
        setMarkupFeedback('');
        setMarkupError('');
        const value = Number(markupInput);
        if (Number.isNaN(value) || value < 0 || value > 100) {
            setMarkupError('Markup must be between 0 and 100.');
            return;
        }
        setIsSavingMarkup(true);
        try {
            const res = await fetch(`${url}/api/business/markup`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify({ markup: value }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'Failed to update markup.');
            setMarkup(data.Business_Markup ?? value);
            setMarkupInput(data.Business_Markup ?? value);
            setMarkupFeedback('Markup updated.');
            setTimeout(() => setMarkupFeedback(''), 3000);
        } catch (err) {
            setMarkupError(err.message);
        } finally {
            setIsSavingMarkup(false);
        }
    };

    const activeBookingCount = bookings.filter((booking) => (
        !['delivered', 'cancelled', 'completed'].includes((booking.Booking_Status || '').toLowerCase())
    )).length;

    const latestBooking = bookings.reduce((latest, booking) => {
        if (!booking.Created_At) return latest;
        if (!latest || new Date(booking.Created_At) > new Date(latest.Created_At)) return booking;
        return latest;
    }, null);

    const filteredBookings = useMemo(() => bookings.filter((booking) => {
        const normalizedStatus = (booking.Booking_Status || '').toLowerCase();
        const matchesStatus = statusFilter === 'all'
            || (statusFilter === 'active' && !['delivered', 'cancelled', 'completed'].includes(normalizedStatus))
            || (statusFilter === 'completed' && ['delivered', 'completed'].includes(normalizedStatus))
            || (statusFilter === 'cancelled' && normalizedStatus === 'cancelled');
        if (!matchesStatus) return false;
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return [
            booking.OwcNumber,
            booking.Booking_Status,
            booking.Selected_Courier,
            booking.EDD,
            booking.Courier_Price,
            booking.P_Weight,
            booking.P_Quantity,
            booking.P_Type,
            booking.GoodsValue,
            booking.DangerousGoods,
            booking.SpecialHandling,
            booking.From_Country,
            booking.From_State,
            booking.From_City,
            booking.From_P_Code,
            booking.From_addr1,
            booking.Sender_Fname,
            booking.Sender_Lname,
            booking.Sender_Email,
            booking.Sender_Phone,
            booking.To_Country,
            booking.To_State,
            booking.To_City,
            booking.To_P_Code,
            booking.To_addr1,
            booking.Rec_Fname,
            booking.Rec_Lname,
            booking.Rec_Email,
            booking.Rec_Phone,
        ].some((field) => field && field.toString().toLowerCase().includes(query));
    }), [bookings, searchQuery, statusFilter]);

    const getResumeStep = (draft) => {
        const hasQuoteData = Array.isArray(draft.quoteData?.Quotes) && draft.quoteData.Quotes.length > 0;
        const hasRequest = Boolean(
            draft.quoteRequestBody?.FromAddress &&
            draft.quoteRequestBody?.ToAddress &&
            Array.isArray(draft.quoteRequestBody?.Items) &&
            draft.quoteRequestBody.Items.length > 0
        );
        const hasSelectedQuote = Boolean(
            draft.selectedQuote?.carrierName &&
            draft.selectedQuote?.price !== undefined
        );
        const hasContacts = Boolean(
            draft.srDetails?.senderFormData &&
            draft.srDetails?.receiverFormData
        );
        if (draft.currentStep === 'review' && hasQuoteData && hasRequest && hasSelectedQuote && hasContacts) return 'review';
        if ((draft.currentStep === 'review' || draft.currentStep === 'details') && hasQuoteData && hasRequest && hasSelectedQuote) return 'details';
        if (hasQuoteData && hasRequest) return 'quotes';
        return 'quote';
    };

    const handleResume = (draft) => {
        restoreDraft(draft);
        const step = getResumeStep(draft);
        navigate({
            quote: '/CalQuote',
            quotes: '/Quotes',
            details: '/SRdetails',
            review: '/Review',
        }[step]);
    };

    const handleDeleteDraft = async (draftId) => {
        if (!window.confirm('Delete this draft? This cannot be undone.')) return;
        setDeletingDraftId(draftId);
        setDraftError('');
        try {
            await deleteDraft(draftId);
            setDrafts((previous) => previous.filter((draft) => draft.id !== draftId));
        } catch (deleteError) {
            setDraftError(deleteError.message || 'Unable to delete this draft.');
        } finally {
            setDeletingDraftId(null);
        }
    };

    const handleUseTemplate = (booking) => {
        applyBookingTemplate(booking);
        navigate('/CalQuote');
    };

    const formatDraftStep = (step) => ({
        quote: 'Quote details',
        quotes: 'Choose courier',
        details: 'Contact details',
        review: 'Review & pay',
    }[step] || 'In progress');

    const formatUpdatedAt = (value) => {
        if (!value) return 'Saved recently';
        return `Updated ${new Intl.DateTimeFormat('en-AU', {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(new Date(value))}`;
    };

    const redirectBooking = location.state?.bookingData;
    const redirectInsuranceStatus = redirectBooking?.insuranceStatus
        ?? redirectBooking?.Insurance?.status
        ?? redirectBooking?.insurance?.status;
    const redirectInsuranceMessage = redirectBooking?.insuranceMessage
        ?? redirectBooking?.Insurance?.message
        ?? redirectBooking?.insurance?.message;
    const showRedirectInsurance = Boolean(
        location.state?.insuranceSelected
        || redirectInsuranceStatus
        || redirectInsuranceMessage
    );

    if (loading) {
        return (
            <main className="bookings-page">
                <div className="container">
                    <div className="bookings-loading" role="status">
                        <span className="loading-line loading-line-wide"></span>
                        <span className="loading-line"></span>
                        <div className="loading-card-grid"><span></span><span></span><span></span></div>
                        <span className="visually-hidden">Loading your bookings…</span>
                    </div>
                </div>
            </main>
        );
    }

    if (error) {
        return (
            <main className="bookings-page">
                <div className="container">
                    <div className="bookings-state-card" role="alert">
                        <span className="section-kicker">ACCOUNT ACCESS</span>
                        <h1>Your shipments are <em>waiting.</em></h1>
                        <p>{error}</p>
                        <button type="button" className="btn btn-smth" onClick={() => navigate('/Login')}>
                            Sign in to continue <span aria-hidden="true">↗</span>
                        </button>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="bookings-page">
            <div className="container">
                <header className="bookings-page-header">
                    <div>
                        <span className="section-kicker">YOUR SHIPPING DESK</span>
                        <h1>Shipments, <em>at a glance.</em></h1>
                        <p>Keep track of active deliveries, revisit saved quotes, and repeat regular shipments in a few clicks.</p>
                    </div>
                    <button type="button" className="btn btn-smth bookings-new-button" onClick={() => navigate('/CalQuote')}>
                        Create a booking <span aria-hidden="true">↗</span>
                    </button>
                </header>

                {location.state?.draftSaved && (
                    <div className="alert alert-success draft-feedback" role="status">Your draft has been saved.</div>
                )}
                <BookingOutcomeNotice state={location.state} />
                {!location.state?.bookingPending && !location.state?.bookingRejected && showRedirectInsurance && (
                    <div className={`alert ${String(redirectInsuranceStatus || '').toLowerCase().includes('fail') ? 'alert-danger' : 'alert-info'} booking-insurance-notice`} role="status">
                        <strong>Insurance status: {redirectInsuranceStatus || 'pending confirmation'}.</strong>
                        <span>{redirectInsuranceMessage || (redirectInsuranceStatus ? 'This is the latest insurance status returned for your booking.' : 'The booking was created; insurance confirmation has not been returned yet.')}</span>
                    </div>
                )}
                {draftError && <div className="alert alert-danger draft-feedback" role="alert">{draftError}</div>}

                <section className="booking-stats" aria-label="Booking summary">
                    <div className="booking-stat booking-stat-highlight">
                        <span className="section-kicker">ALL BOOKINGS</span>
                        <strong>{bookings.length}</strong>
                        <span>shipments in your history</span>
                    </div>
                    <div className="booking-stat">
                        <span className="section-kicker">IN MOTION</span>
                        <strong>{activeBookingCount}</strong>
                        <span>active or processing</span>
                    </div>
                    <div className="booking-stat">
                        <span className="section-kicker">SAVED FOR LATER</span>
                        <strong>{drafts.length}</strong>
                        <span>{drafts.length === 1 ? 'draft ready' : 'drafts ready'}</span>
                    </div>
                    {isBusiness ? (
                        <form className="booking-stat booking-stat-markup" onSubmit={handleMarkupSave}>
                            <span className="section-kicker">YOUR MARKUP</span>
                            <strong>{markup !== '' ? `${markup}%` : '0%'}</strong>
                            <span>applied to your quotes</span>
                            <div className="admin-markup-controls" style={{ marginTop: 'auto', paddingTop: '10px' }}>
                                <label htmlFor="biz-markup">Update percentage</label>
                                <div>
                                    <input
                                        id="biz-markup"
                                        type="number"
                                        value={markupInput}
                                        onChange={(e) => { setMarkupInput(e.target.value); setMarkupError(''); setMarkupFeedback(''); }}
                                        min="0"
                                        max="100"
                                        step="0.1"
                                    />
                                    <button type="submit" className="btn btn-smth" disabled={isSavingMarkup}>
                                        {isSavingMarkup ? 'Saving…' : 'Save'}
                                    </button>
                                </div>
                            </div>
                            {markupError && <p style={{ fontSize: '.72rem', color: '#c0392b', margin: '.4rem 0 0', fontWeight: 600 }}>{markupError}</p>}
                            {markupFeedback && <p style={{ fontSize: '.72rem', color: 'var(--deep-green, #164e43)', margin: '.4rem 0 0', fontWeight: 600 }}>{markupFeedback}</p>}
                        </form>
                    ) : (
                        <div className="booking-stat">
                            <span className="section-kicker">LAST BOOKED</span>
                            <strong>{latestBooking ? formatDate(latestBooking.Created_At) : '—'}</strong>
                            <span>your latest shipment</span>
                        </div>
                    )}
                </section>

                {isBusiness && bizProfile && (
                    <section className="biz-profile-section" aria-label="Business account">
                        <div className="bookings-section-heading">
                            <div>
                                <span className="section-kicker">BUSINESS ACCOUNT</span>
                                <h2>Your <em>account.</em></h2>
                            </div>
                            <p>Your account details and pricing markup applied to all quotes.</p>
                        </div>
                        <div className="biz-profile-cards">
                            <div className="biz-profile-card">
                                <span className="section-kicker">ACCOUNT NAME</span>
                                <strong>{bizProfile.Name}</strong>
                                <span>{bizProfile.Email}</span>
                            </div>
                            <div className="biz-profile-card">
                                <span className="section-kicker">ACCOUNT TYPE</span>
                                <strong>Business</strong>
                                <span>ID #{bizProfile.Cust_ID}</span>
                            </div>
                            <div className="biz-profile-card">
                                <span className="section-kicker">MEMBER SINCE</span>
                                <strong>{bizProfile.Created_At ? new Date(bizProfile.Created_At).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</strong>
                                <span>Account created</span>
                            </div>
                        </div>

                    </section>
                )}

                <section className="booking-history-section">
                    <div className="bookings-section-heading bookings-history-heading">
                        <div>
                            <span className="section-kicker">SHIPMENT HISTORY</span>
                            <h2>Your <em>bookings.</em></h2>
                        </div>
                        <p>Everything you need to find, understand, and reuse a past shipment.</p>
                    </div>

                    <div className="bookings-toolbar">
                        <div className="booking-filters" role="group" aria-label="Filter bookings">
                            {[
                                ['all', 'All', bookings.length],
                                ['active', 'Active', activeBookingCount],
                                ['completed', 'Completed', bookings.filter((booking) => ['delivered', 'completed'].includes((booking.Booking_Status || '').toLowerCase())).length],
                                ['drafts', 'Drafts', drafts.length],
                            ].map(([value, label, count]) => (
                                <button
                                    type="button"
                                    key={value}
                                    className={`booking-filter ${statusFilter === value ? 'is-active' : ''}`}
                                    onClick={() => { setStatusFilter(value); setSearchQuery(''); }}
                                    aria-pressed={statusFilter === value}
                                >
                                    {label} <span>{count}</span>
                                </button>
                            ))}
                        </div>
                        {statusFilter !== 'drafts' && (
                            <label className="booking-search">
                                <span className="visually-hidden">Search bookings</span>
                                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <path d="m21 21-4.35-4.35"></path>
                                </svg>
                                <input
                                    type="search"
                                    placeholder="Search tracking, name, email…"
                                    value={searchQuery}
                                    onChange={(event) => setSearchQuery(event.target.value)}
                                />
                                {searchQuery && (
                                    <button type="button" className="booking-search-clear" onClick={() => setSearchQuery('')} aria-label="Clear search">×</button>
                                )}
                            </label>
                        )}
                    </div>

                    {statusFilter === 'drafts' ? (
                        drafts.length === 0 ? (
                            <div className="draft-empty">
                                <div>
                                    <strong>No saved drafts yet.</strong>
                                    <span>Start a booking and choose "Save draft &amp; exit" whenever you need more time.</span>
                                </div>
                                <button type="button" className="draft-save-btn" onClick={() => navigate('/CalQuote')}>Start a quote ↗</button>
                            </div>
                        ) : (
                            <div className="draft-list">
                                {drafts.map((draft) => {
                                    const from = draft.formData?.pickupCity || draft.formData?.pickupAddress || 'Pickup not set';
                                    const to = draft.formData?.deliveryCity || draft.formData?.deliveryAddress || 'Delivery not set';
                                    return (
                                        <article className="draft-card" key={draft.id}>
                                            <div>
                                                <div className="draft-card-top">
                                                    <div>
                                                        <span className="booking-card-label">SAVED SHIPMENT</span>
                                                        <h3>{draft.title}</h3>
                                                    </div>
                                                    <span className="draft-step">{formatDraftStep(draft.currentStep)}</span>
                                                </div>
                                                <div className="draft-route"><span>{from}</span><i aria-hidden="true">→</i><span>{to}</span></div>
                                                <span className="draft-updated">{formatUpdatedAt(draft.updatedAt)}</span>
                                            </div>
                                            <div className="draft-card-actions">
                                                <button
                                                    type="button"
                                                    className="draft-delete-btn"
                                                    onClick={() => handleDeleteDraft(draft.id)}
                                                    disabled={deletingDraftId === draft.id}
                                                >
                                                    {deletingDraftId === draft.id ? 'Deleting…' : 'Delete draft'}
                                                </button>
                                                <button type="button" className="btn btn-smth" onClick={() => handleResume(draft)}>
                                                    Continue draft <span aria-hidden="true">↗</span>
                                                </button>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        )
                    ) : (
                        <>
                    {bookings.length === 0 && (
                        <div className="bookings-state-card bookings-empty-card">
                            <span className="section-kicker">READY WHEN YOU ARE</span>
                            <h3>Your first shipment starts here.</h3>
                            <p>Get a live courier comparison and book your delivery in a few simple steps.</p>
                            <button type="button" className="btn btn-smth" onClick={() => navigate('/CalQuote')}>Get a quote ↗</button>
                        </div>
                    )}
                    {bookings.length > 0 && filteredBookings.length === 0 && (
                        <div className="booking-no-results">
                            <strong>No bookings match your search.</strong>
                            <span>Try another tracking number, contact name, or filter.</span>
                            <button type="button" className="draft-save-btn" onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}>Clear filters</button>
                        </div>
                    )}

                    <div className="booking-card-list">
                        {filteredBookings.map((booking, index) => {
                            const status = booking.Booking_Status || 'Pending';
                            const pickupAddress = [booking.From_addr1, booking.From_addr2].filter(Boolean).join(', ');
                            const deliveryAddress = [booking.To_addr1, booking.To_addr2].filter(Boolean).join(', ');
                            const dimensions = [booking.D_Length, booking.D_Width, booking.D_Height]
                                .every((value) => value !== null && value !== undefined && value !== '')
                                ? `${booking.D_Length} × ${booking.D_Width} × ${booking.D_Height} cm`
                                : 'Not provided';
                            const insurance = booking.Insurance ?? booking.insurance ?? null;
                            const insuranceStatus = insurance?.status || insurance?.Status || 'Pending confirmation';
                            const insuranceMessage = insurance?.message || insurance?.Message;
                            return (
                                <article key={`${booking.OwcNumber || booking.Booking_ID || 'booking'}-${index}`} className="booking-card">
                                    <header className="booking-card-header">
                                        <div>
                                            <span className="booking-card-label">TRACKING ID</span>
                                            <h3>#{valueOrFallback(booking.OwcNumber, 'Pending')}</h3>
                                        </div>
                                        <div className="booking-card-actions">
                                            {booking.OwcNumber && (
                                                <button
                                                    type="button"
                                                    className="booking-text-action"
                                                    onClick={() => navigate('/Tracking', { state: { trackingNumber: booking.OwcNumber } })}
                                                >
                                                    Track shipment ↗
                                                </button>
                                            )}
                                            <span className={`booking-status booking-status-${statusClass(status)}`}><i></i>{status}</span>
                                        </div>
                                    </header>

                                    <div className="booking-route-card">
                                        <div className="booking-route-point">
                                            <span className="booking-card-label">FROM</span>
                                            <strong>{valueOrFallback(booking.From_City, 'Origin not set')}</strong>
                                            <span>{getRouteLabel(booking.From_Country, null, booking.From_P_Code)}</span>
                                            {pickupAddress && <small>{pickupAddress}</small>}
                                        </div>
                                        <div className="booking-route-line" aria-hidden="true"><span></span><i>→</i><span></span></div>
                                        <div className="booking-route-point booking-route-destination">
                                            <span className="booking-card-label">TO</span>
                                            <strong>{valueOrFallback(booking.To_City, 'Destination not set')}</strong>
                                            <span>{getRouteLabel(booking.To_Country, null, booking.To_P_Code)}</span>
                                            {deliveryAddress && <small>{deliveryAddress}</small>}
                                        </div>
                                    </div>

                                    <div className="booking-key-facts">
                                        <div><span className="booking-card-label">COURIER</span><strong>{valueOrFallback(booking.Selected_Courier)}</strong></div>
                                        <div><span className="booking-card-label">EST. DELIVERY</span><strong>{valueOrFallback(booking.EDD, 'To be confirmed')}</strong></div>
                                        <div><span className="booking-card-label">PAID</span><strong>{formatMoney(booking.Courier_Price)}</strong></div>
                                        <div><span className="booking-card-label">PAYMENT</span><strong>{valueOrFallback(booking.Payment_Status, 'Paid')}</strong></div>
                                    </div>

                                    <div className="booking-detail-section">
                                        <div className="booking-detail-heading">
                                            <span className="section-kicker">SHIPMENT DETAILS</span>
                                            <span>Package information</span>
                                        </div>
                                        <div className="booking-spec-grid">
                                            <div><img src={weight} alt="" /><span>Weight<strong>{valueOrFallback(booking.P_Weight)} kg</strong></span></div>
                                            <div><img src={quantity} alt="" /><span>Quantity<strong>{valueOrFallback(booking.P_Quantity)} {Number(booking.P_Quantity) === 1 ? 'item' : 'items'}</strong></span></div>
                                            <div><img src={box} alt="" /><span>Package<strong>{valueOrFallback(booking.P_Type)}</strong></span></div>
                                            <div><img src={length} alt="" /><span>Dimensions<strong>{dimensions}</strong></span></div>
                                            <div><span className="booking-spec-icon">◈</span><span>Goods value<strong>{formatMoney(booking.GoodsValue)}</strong></span></div>
                                            <div><span className="booking-spec-icon">!</span><span>Special handling<strong>{valueOrFallback(booking.SpecialHandling, 'None')}</strong></span></div>
                                        </div>
                                        <div className="booking-flags">
                                            <span className={booking.isDangerous ? 'is-on' : ''}>
                                                {booking.isDangerous ? `Dangerous goods: ${valueOrFallback(booking.DangerousGoods, 'Other')}` : 'No dangerous goods'}
                                            </span>
                                            <span className={booking.Stackability === true || booking.Stackability === 'true' ? 'is-on' : ''}>{booking.Stackability === true || booking.Stackability === 'true' ? 'Stackable' : 'Non-stackable'}</span>
                                        </div>
                                    </div>

                                    <div className="booking-insurance-detail">
                                        <div className="booking-detail-heading">
                                            <span className="section-kicker">INSURANCE</span>
                                            <span>Shipment protection status</span>
                                        </div>
                                        {insurance ? (
                                            <div className="booking-insurance-content">
                                                <span className={`booking-status booking-status-${statusClass(insuranceStatus)}`}><i></i>{insuranceStatus}</span>
                                                <div className="booking-insurance-facts">
                                                    <div><span className="booking-card-label">COVERAGE</span><strong>{formatMoney(insurance.coverage)}</strong></div>
                                                    <div><span className="booking-card-label">CATEGORY</span><strong>{valueOrFallback(insurance.category)}</strong></div>
                                                    <div><span className="booking-card-label">CONDITION</span><strong>{valueOrFallback(insurance.condition)}</strong></div>
                                                    <div><span className="booking-card-label">PREMIUM</span><strong>{formatMoney(insurance.premium)}</strong></div>
                                                </div>
                                                {insuranceMessage && <p className="booking-insurance-message">{insuranceMessage}</p>}
                                            </div>
                                        ) : (
                                            <p className="booking-insurance-message">No paid shipment insurance was selected.</p>
                                        )}
                                    </div>

                                    <div className="booking-contacts">
                                        <div className="booking-contact">
                                            <span className="booking-card-label">SENDER</span>
                                            <strong>{[booking.Sender_Fname, booking.Sender_Lname].filter(Boolean).join(' ') || 'Not provided'}</strong>
                                            <span>{valueOrFallback(booking.Sender_Email)}</span>
                                            <span>{valueOrFallback(booking.Sender_Phone)}</span>
                                        </div>
                                        <div className="booking-contact">
                                            <span className="booking-card-label">RECIPIENT</span>
                                            <strong>{[booking.Rec_Fname, booking.Rec_Lname].filter(Boolean).join(' ') || 'Not provided'}</strong>
                                            <span>{valueOrFallback(booking.Rec_Email)}</span>
                                            <span>{valueOrFallback(booking.Rec_Phone)}</span>
                                        </div>
                                    </div>

                                    <footer className="booking-card-footer">
                                        <span>Booked {formatDate(booking.Created_At)}</span>
                                        <button type="button" className="btn btn-smth" onClick={() => handleUseTemplate(booking)}>
                                            Use as template <span aria-hidden="true">↗</span>
                                        </button>
                                    </footer>
                                </article>
                            );
                        })}
                    </div>
                        </>
                    )}
                </section>
            </div>
        </main>
    );
}

export default Bookings;